import { Container, type ContainerOptions } from "../core/Container.js";
import type { Control } from "../core/Control.js";
import type { WheelEventArgs } from "../core/events.js";
import { clamp, type Point, type Rect, type Size } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { ScrollableBase, type ScrollableOptions } from "./ScrollableBase.js";

/**
 * The panel user content is placed into. It fills the viewport and carries the
 * scroll offset and zoom, so its children stay in unscaled content coordinates
 * and never need to know the viewer exists.
 */
export class ScrollContent extends Container {
  /** Explicit extent; when null the extent is the union of the children. */
  contentWidth: number | null = null;
  contentHeight: number | null = null;

  constructor(options: ContainerOptions = {}) {
    super(options);
  }

  /** Content extent in unscaled units. */
  extent(): Size {
    if (this.contentWidth !== null && this.contentHeight !== null) {
      return { width: this.contentWidth, height: this.contentHeight };
    }
    let right = 0;
    let bottom = 0;
    for (const c of this.controls) {
      if (!c.visible) continue;
      right = Math.max(right, c.x + c.width);
      bottom = Math.max(bottom, c.y + c.height);
    }
    return {
      width: this.contentWidth ?? right,
      height: this.contentHeight ?? bottom,
    };
  }

  /** Transparent: the viewer paints the background behind it. */
  protected override onPaint(): void {}

  protected override hitTestSelf(): boolean {
    return false;
  }
}

export interface ScrollViewerOptions extends ScrollableOptions {
  zoom?: number;
  minZoom?: number;
  maxZoom?: number;
  /** Allow ctrl+wheel and the zoom API to change scale. */
  zoomEnabled?: boolean;
  /** Paint the theme's sunken panel behind the content. */
  showBorder?: boolean;
}

const ZOOM_STEP = 1.1;

/**
 * A scrolling, zooming viewport over arbitrary child controls.
 *
 * Zoom is a uniform scale applied by the content panel's transform, so text
 * inside is re-rasterized at the zoomed size rather than magnified — see
 * RenderContext.drawText. Because the transform is translate + scale only,
 * every clip stays axis-aligned and the scissor-rect clipping still holds.
 */
export class ScrollViewer extends ScrollableBase {
  readonly content: ScrollContent;

  #zoom: number;
  minZoom: number;
  maxZoom: number;
  zoomEnabled: boolean;
  showBorder: boolean;

  constructor(options: ScrollViewerOptions = {}) {
    super({ width: 320, height: 240, ...options });
    this.#zoom = options.zoom ?? 1;
    this.minZoom = options.minZoom ?? 0.25;
    this.maxZoom = options.maxZoom ?? 4;
    this.zoomEnabled = options.zoomEnabled ?? true;
    this.showBorder = options.showBorder ?? true;

    this.content = new ScrollContent();
    // Insert below the scroll bars so they paint on top and hit-test first.
    this.controls.insert(0, this.content);
    this.#syncContent();
  }

  /** Convenience: `viewer.add(control)` puts it in the content panel. */
  add<T extends Control>(control: T): T {
    const added = this.content.controls.add(control);
    this.updateScrollBars();
    this.#syncContent();
    return added;
  }

  get zoom(): number {
    return this.#zoom;
  }
  set zoom(v: number) {
    this.setZoom(v);
  }

  /**
   * Change the zoom, keeping the content point under `anchor` (a local point,
   * defaulting to the viewport center) stationary on screen.
   */
  setZoom(value: number, anchor?: Point): void {
    if (!this.zoomEnabled) return;
    const next = clamp(value, this.minZoom, this.maxZoom);
    if (next === this.#zoom) return;

    const view = this.viewportRect;
    const point = anchor ?? { x: view.x + view.width / 2, y: view.y + view.height / 2 };
    // Content coordinate currently under the anchor.
    const cx = (this.scrollOffsetX + point.x - view.x) / this.#zoom;
    const cy = (this.scrollOffsetY + point.y - view.y) / this.#zoom;

    this.#zoom = next;
    this.updateScrollBars();
    // Put that same content coordinate back under the anchor.
    this.scrollOffsetX = cx * next - (point.x - view.x);
    this.scrollOffsetY = cy * next - (point.y - view.y);
    this.#syncContent();
    this.emit("zoom");
    this.invalidate();
  }

  /**
   * Pinch-to-zoom, anchored between the fingers.
   *
   * False once the scale is pinned at `minZoom` or `maxZoom`, which is what
   * lets a pinch that has bottomed out reach a viewer further out.
   */
  override zoomBy(factor: number, at: Point): boolean {
    if (!this.zoomEnabled || factor === 1) return false;
    const before = this.#zoom;
    this.setZoom(before * factor, at);
    return this.#zoom !== before;
  }

  zoomIn(anchor?: Point): void {
    this.setZoom(this.#zoom * ZOOM_STEP, anchor);
  }

  zoomOut(anchor?: Point): void {
    this.setZoom(this.#zoom / ZOOM_STEP, anchor);
  }

  resetZoom(): void {
    this.setZoom(1);
  }

  /** Scale so the whole content fits, then center it. */
  zoomToFit(): void {
    const view = this.viewportRect;
    const extent = this.content.extent();
    if (extent.width <= 0 || extent.height <= 0 || view.width <= 0 || view.height <= 0) return;
    const fit = Math.min(view.width / extent.width, view.height / extent.height);
    this.#zoom = clamp(fit, this.minZoom, this.maxZoom);
    this.updateScrollBars();
    this.scrollOffsetX = (extent.width * this.#zoom - view.width) / 2;
    this.scrollOffsetY = (extent.height * this.#zoom - view.height) / 2;
    this.#syncContent();
    this.emit("zoom");
    this.invalidate();
  }

  protected override contentSize(_theme: Theme): Size {
    const extent = this.content.extent();
    return { width: extent.width * this.#zoom, height: extent.height * this.#zoom };
  }

  /** Push the viewer's viewport, offset and zoom onto the content panel. */
  #syncContent(): void {
    const view = this.viewportRect;
    this.content.setBounds(view.x, view.y, view.width, view.height);
    this.content.childScale = this.#zoom;
    // Container scroll offsets are in child-space units, so undo the zoom.
    this.content.scrollX = this.scrollOffsetX / this.#zoom;
    this.content.scrollY = this.scrollOffsetY / this.#zoom;
  }

  /**
   * The bars are decided against the content's extent, and then the content panel is re-sized,
   * which re-stretches any child aligned to fill it. A stretched child that was wider than the
   * new viewport turns the bars on and then shrinks to fit inside them, so ask again until the
   * extent stops moving. Three passes covers bars on, then off.
   */
  override updateScrollBars(): void {
    for (let pass = 0; pass < 3; pass++) {
      const before = this.content.extent();
      super.updateScrollBars();
      this.#syncContent();
      const after = this.content.extent();
      if (after.width === before.width && after.height === before.height) break;
    }
  }

  override notifyEnvironmentChanged(): void {
    super.notifyEnvironmentChanged();
    this.#syncContent();
  }

  protected override onScrollChanged(): void {
    this.#syncContent();
    super.onScrollChanged();
  }

  protected override onWheel(e: WheelEventArgs): void {
    if (e.ctrlKey && this.zoomEnabled) {
      // Anchor the zoom at the pointer, the way every map and image viewer does.
      const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
      this.setZoom(this.#zoom * factor, { x: e.x, y: e.y });
      e.handled = true;
      return;
    }
    super.onWheel(e);
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    if (this.showBorder) {
      theme.drawPanel(rc, this.localRect, true);
    } else {
      rc.fillRect(this.localRect, theme.palette.fieldBg);
    }
  }

  protected override onPaintOverlay(rc: RenderContext, theme: Theme): void {
    this.paintScrollCorner(rc, theme);
  }

  /** Content-space rectangle currently visible, useful for culling. */
  get visibleContentRect(): Rect {
    const view = this.viewportRect;
    return {
      x: this.scrollOffsetX / this.#zoom,
      y: this.scrollOffsetY / this.#zoom,
      width: view.width / this.#zoom,
      height: view.height / this.#zoom,
    };
  }
}
